import { useEffect, useState, useMemo } from "react";
import * as _ from "lodash";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useProductStore } from "@/_zustand/store";
import { useMachine } from "@/_zustand/machine";
import { useMaquinaById } from "@/hooks/useMaquinas";
import trashIcon from "@/assets/imgs/trash_icon.png";
import "./CarritoModal.css";

export const ModalCarrito = ({ show, handleClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCompanyFlow = location.pathname.startsWith("/company");
  const { allQuantity, calculateTotals, products, clearCartByMachine } =
    useProductStore();
  const { setMachine } = useMachine();
  const { machineSelected, empresa_id } = useMachine();
  const { mutateAsync: getMaquinaByIdMutateAsync } = useMaquinaById();
  const [machineId] = useState(0);

  // Incidencia #3: reemplazar useEffect+useState por useMemo para que listByMachine
  // se recalcule de forma síncrona cada vez que cambie products, evitando el
  // parpadeo de "carrito vacío" y la falta de actualización ante cambios en products.
  const listByMachine = useMemo(() => {
    return products.reduce((acumulador, itemActual) => {
      const { maquina_id, empresa_id: productEmpresaId } = itemActual;

      if (Number(itemActual.producto_cant) <= 0) {
        return acumulador;
      }

      // Si es flujo de empresa, filtrar solo los productos de esa empresa
      if (isCompanyFlow && empresa_id && productEmpresaId != empresa_id) {
        return acumulador;
      }

      if (!acumulador[maquina_id]) {
        acumulador[maquina_id] = [];
      }
      acumulador[maquina_id].push(itemActual);
      return acumulador;
    }, {});
  }, [products, isCompanyFlow, empresa_id]);

  useEffect(() => {
    calculateTotals(machineSelected);
  }, [machineSelected, allQuantity, calculateTotals]);

  useEffect(() => {
    if (machineId != 0 && machineSelected != null)
      getMaquinaByIdMutateAsync(machineSelected).then((resp) => {
        if (_.size(resp) == 0) {
          //navigate al home porque la maquina no se encontro
          toast.error("Hay un error con esta máquina.");
          navigate(isCompanyFlow ? "/" : `/`, { replace: false }); // Fallback to root if can't determine company ID easily, or just root.
          return;
        }
        if (resp[0].estado_id != 1) {
          //navigate al home no esta activa
          toast.error("Esta máquina no se encuentra activa.");
          navigate(`/`, { replace: false });
          return;
        }

        let maquinaResp = resp[0];
        // setLogoUrl(maquinaResp.maquinamodelo_icono);
        let maquinaMap = {
          comuna_nombre: maquinaResp.comuna_nombre,
          region_nombre: maquinaResp.region_nombre,
          empresa_id: maquinaResp.empresa_id,
          empresa_nombre: maquinaResp.empresa_nombre,
          empresa_rut: "",
          maquina_direccion: maquinaResp.maquina_direccion,
          maquina_latitud: maquinaResp.maquina_latitud,
          maquina_longitud: maquinaResp.maquina_longitud,
          maquina_nombre: maquinaResp.maquina_nombre,
          maquinamodelo_icono: maquinaResp.maquinamodelo_icono,
          oneclick_apikey: maquinaResp.oneclick_apikey,
          oneclick_codigo_comercio: maquinaResp.oneclick_codigo_comercio,
          oneclick_codigo_tienda: maquinaResp.oneclick_codigo_tienda,
          webpay_apikey: maquinaResp.webpay_apikey,
          webpay_codigo_comercio: maquinaResp.webpay_codigo_comercio,
          maquina_id: maquinaResp.maquina_id,
          vende_licor: maquinaResp.vende_licor,
          maquina_telefono: maquinaResp.maquina_telefono,
        };

        setMachine(maquinaMap);
        navigate(isCompanyFlow ? `/company/cart` : `/cart`, { replace: false });
      });
    //}
  }, [
    machineId,
    machineSelected,
    isCompanyFlow,
    navigate,
    setMachine,
    getMaquinaByIdMutateAsync,
  ]);

  const handleClickCarrito = async (machineId) => {
    try {
      // 1. Obtener la respuesta de la máquina
      const resp = await getMaquinaByIdMutateAsync(machineId);

      // 2. Validaciones iniciales
      if (_.size(resp) === 0) {
        toast.error("Hay un error con esta máquina.");
        navigate(`/`, { replace: false });
        return;
      }

      const maquinaResp = resp[0]; // Asumimos que siempre habrá un elemento si _size(resp) > 0

      if (maquinaResp.estado_id !== 1) {
        toast.error("Esta máquina no se encuentra activa.");
        navigate(`/`, { replace: false });
        return;
      }

      // 3. Mapear los datos de la máquina
      const maquinaMap = {
        comuna_nombre: maquinaResp.comuna_nombre,
        region_nombre: maquinaResp.region_nombre,
        empresa_id: maquinaResp.empresa_id,
        empresa_nombre: maquinaResp.empresa_nombre,
        empresa_rut: "", // Asegúrate de que este campo se necesite o elimínalo si no aplica
        maquina_direccion: maquinaResp.maquina_direccion,
        maquina_latitud: maquinaResp.maquina_latitud,
        maquina_longitud: maquinaResp.maquina_longitud,
        maquina_nombre: maquinaResp.maquina_nombre,
        maquinamodelo_icono: maquinaResp.maquinamodelo_icono,
        oneclick_apikey: maquinaResp.oneclick_apikey,
        oneclick_codigo_comercio: maquinaResp.oneclick_codigo_comercio,
        oneclick_codigo_tienda: maquinaResp.oneclick_codigo_tienda,
        webpay_apikey: maquinaResp.webpay_apikey,
        webpay_codigo_comercio: maquinaResp.webpay_codigo_comercio,
        maquina_id: maquinaResp.maquina_id,
        vende_licor: maquinaResp.vende_licor,
        maquina_telefono: maquinaResp.maquina_telefono,
      };

      // 4. Asignar al estado y navegar
      setMachine(maquinaMap);

      // Navegación inmediata después de setMachine
      if (
        location.pathname === "/cart" ||
        location.pathname === "/company/cart"
      ) {
        window.location.reload(); // Recarga la página
      }
      navigate(isCompanyFlow ? `/company/cart` : `/cart`, { replace: true });
      handleClose();
    } catch (error) {
      // Manejo de errores de la llamada al servicio
      console.error("Error al obtener datos de la máquina:", error);
      toast.error("Ocurrió un error al cargar la información de la máquina.");
      navigate(`/`, { replace: false }); // Redirigir a home en caso de cualquier error no manejado
    }
  };

  const handleDeleteCart = async (maquina_id, empresa_nombre) => {
    const result = await Swal.fire({
      title: `¿Eliminar carrito de ${empresa_nombre}?`,
      text: "Se eliminarán todos los productos de este carrito.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });
    if (result.isConfirmed) {
      clearCartByMachine(maquina_id);
      toast.success("Carrito eliminado correctamente");
    }
  };

  const machineClick = async (machine) => {
    const machineId = machine[0].maquina_id;
    try {
      // 1. Obtener la respuesta de la máquina
      const resp = await getMaquinaByIdMutateAsync(machineId);

      // 2. Validaciones iniciales
      if (_.size(resp) === 0) {
        toast.error("Hay un error con esta máquina.");
        navigate(`/`, { replace: false });
        return;
      }

      const maquinaResp = resp[0]; // Asumimos que siempre habrá un elemento si _size(resp) > 0

      if (maquinaResp.estado_id !== 1) {
        toast.error("Esta máquina no se encuentra activa.");
        navigate(`/`, { replace: false });
        return;
      }

      // 3. Mapear los datos de la máquina
      const maquinaMap = {
        comuna_nombre: maquinaResp.comuna_nombre,
        region_nombre: maquinaResp.region_nombre,
        empresa_id: maquinaResp.empresa_id,
        empresa_nombre: maquinaResp.empresa_nombre,
        empresa_rut: "", // Asegúrate de que este campo se necesite o elimínalo si no aplica
        maquina_direccion: maquinaResp.maquina_direccion,
        maquina_latitud: maquinaResp.maquina_latitud,
        maquina_longitud: maquinaResp.maquina_longitud,
        maquina_nombre: maquinaResp.maquina_nombre,
        maquinamodelo_icono: maquinaResp.maquinamodelo_icono,
        oneclick_apikey: maquinaResp.oneclick_apikey,
        oneclick_codigo_comercio: maquinaResp.oneclick_codigo_comercio,
        oneclick_codigo_tienda: maquinaResp.oneclick_codigo_tienda,
        webpay_apikey: maquinaResp.webpay_apikey,
        webpay_codigo_comercio: maquinaResp.webpay_codigo_comercio,
        maquina_id: maquinaResp.maquina_id,
        vende_licor: maquinaResp.vende_licor,
        maquina_telefono: maquinaResp.maquina_telefono,
      };

      // 4. Asignar al estado y navegar
      setMachine(maquinaMap);
      // Navegación inmediata después de setMachine
      navigate(
        isCompanyFlow ? `/company/store/${machineId}` : `/store/${machineId}`,
        { replace: false },
      );
      handleClose();
    } catch (error) {
      // Manejo de errores de la llamada al servicio
      console.error("Error al obtener datos de la máquina:", machine);
      toast.error("Ocurrió un error al cargar la información de la máquina.");
      navigate(`/`, { replace: false }); // Redirigir a home en caso de cualquier error no manejado
    }
  };

  if (show)
    return (
      <div className="order-detail-modal-overlay">
        <div className="modal-content-cart">
          <div className="custom-modal">
            <div className="modal-header">
              <h5 className="modal-title">Carrito</h5>
              <button type="button" className="close" onClick={handleClose}>
                &times;
              </button>
            </div>

            {products.length > 0 ? (
              <div className="modal-carrito-list">
                {Object.values(listByMachine).length > 0 ? (
                  Object.values(listByMachine).map((item, index) => (
                    <div key={index} className="modal-carrito-item">
                      <img
                        src={item[0].logo_machine}
                        alt={item[0].title}
                        className="modal-carrito-img"
                      />
                      <div className="modal-carrito-info">
                        <p className="modal-carrito-title">
                          {item[0].name_empresa}
                        </p>
                        <p className="modal-carrito-articles">
                          {item.length} artículos
                        </p>
                        <p className="modal-carrito-location">
                          📍 <span className="distance">13 km</span>
                        </p>
                        <p className="modal-carrito-address">
                          {item[0].address_machine}
                        </p>
                        <div className="modal-carrito-buttons">
                          <Button
                            variant="outline-success"
                            className="btn-store"
                            onClick={() => machineClick(item)}
                          >
                            Ver tienda
                          </Button>
                          <Button
                            variant="success"
                            className="btn-cart"
                            onClick={() =>
                              handleClickCarrito(item[0].maquina_id)
                            }
                          >
                            Ver carrito
                          </Button>
                        </div>
                      </div>
                      <button
                        className="btn-delete-cart"
                        title="Eliminar carrito"
                        onClick={() =>
                          handleDeleteCart(
                            item[0].maquina_id,
                            item[0].name_empresa,
                          )
                        }
                      >
                        <img
                          src={trashIcon}
                          alt="Eliminar"
                          className="delete-cart-icon"
                        />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="empty-cart-message">
                    <p className="empty-cart-title">
                      ¡Aún no tienes productos en tus carritos!
                    </p>
                    <p className="empty-cart-hint">
                      Explora nuestras tiendas y agrega los productos que más te
                      gusten.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-cart-message">
                <p className="empty-cart-title">
                  ¡Aún no tienes productos en tus carritos!
                </p>
                <p className="empty-cart-hint">
                  Explora nuestras tiendas y agrega los productos que más te
                  gusten.
                </p>
              </div>
            )}
          </div>
        </div>{" "}
      </div>
    );
};
